<template>
  <div>
    <NavigationBar/>
    <div class="content-minimize">
      <div class="container-fluid">
        <!-- Page header -->
        <div class="row mb-4">
          <div class="col-12">
            <div class="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 class="mb-sm-0 font-size-18">Payroll Setup Management</h4>
              <div class="page-title-right">
                <ol class="breadcrumb m-0">
                  <li class="breadcrumb-item"><a href="javascript:void(0)">Payroll</a></li>
                  <li class="breadcrumb-item active">Setup Management</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <!-- Setup categories -->
        <div class="row">
          <!-- Pay Grades -->
          <div class="col-xl-4 col-md-6 mb-4">
            <div class="card h-100">
              <div class="card-header bg-transparent">
                <div class="d-flex justify-content-between align-items-center">
                  <h5 class="card-title mb-0">Pay Grades</h5>
                  <button @click="showPayGradesModal" class="btn btn-sm btn-primary">
                    <i class="mdi mdi-cog-outline me-1"></i> Manage
                  </button>
                </div>
              </div>
              <div class="card-body">
                <div class="text-center py-3">
                  <i class="mdi mdi-certificate-outline text-primary" style="font-size: 48px;"></i>
                  <h6 class="mt-3">Pay Grade Management</h6>
                  <p class="text-muted mb-0">Configure salary ranges and pay scales for different job grades</p>
                </div>
                <div class="mt-3">
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Define minimum and maximum salary ranges</p>
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Set midpoint salary values</p>
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Link grades to salary structures</p>
                </div>
                <div class="mt-3 text-center">
                  <button @click="showPayGradesModal" class="btn btn-outline-primary btn-sm">
                    <i class="mdi mdi-plus me-1"></i> Configure Pay Grades
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Allowance Types -->
          <div class="col-xl-4 col-md-6 mb-4">
            <div class="card h-100">
              <div class="card-header bg-transparent">
                <div class="d-flex justify-content-between align-items-center">
                  <h5 class="card-title mb-0">Allowance Types</h5>
                  <button @click="showAllowanceTypesModal" class="btn btn-sm btn-primary">
                    <i class="mdi mdi-cog-outline me-1"></i> Manage
                  </button>
                </div>
              </div>
              <div class="card-body">
                <div class="text-center py-3">
                  <i class="mdi mdi-cash-plus text-success" style="font-size: 48px;"></i>
                  <h6 class="mt-3">Allowance Type Management</h6>
                  <p class="text-muted mb-0">Setup different types of allowances for employees</p>
                </div>
                <div class="mt-3">
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Configure fixed amount allowances</p>
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Set percentage-based allowances</p>
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Define taxable status of allowances</p>
                </div>
                <div class="mt-3 text-center">
                  <button @click="showAllowanceTypesModal" class="btn btn-outline-primary btn-sm">
                    <i class="mdi mdi-plus me-1"></i> Configure Allowances
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Deduction Types -->
          <div class="col-xl-4 col-md-6 mb-4">
            <div class="card h-100">
              <div class="card-header bg-transparent">
                <div class="d-flex justify-content-between align-items-center">
                  <h5 class="card-title mb-0">Deduction Types</h5>
                  <button @click="showDeductionTypesModal" class="btn btn-sm btn-primary">
                    <i class="mdi mdi-cog-outline me-1"></i> Manage
                  </button>
                </div>
              </div>
              <div class="card-body">
                <div class="text-center py-3">
                  <i class="mdi mdi-cash-minus text-danger" style="font-size: 48px;"></i>
                  <h6 class="mt-3">Deduction Type Management</h6>
                  <p class="text-muted mb-0">Setup different types of deductions for employees</p>
                </div>
                <div class="mt-3">
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Configure fixed amount deductions</p>
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Set percentage-based deductions</p>
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Define pretax and mandatory status</p>
                </div>
                <div class="mt-3 text-center">
                  <button @click="showDeductionTypesModal" class="btn btn-outline-primary btn-sm">
                    <i class="mdi mdi-plus me-1"></i> Configure Deductions
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Tax Brackets -->
          <div class="col-xl-4 col-md-6 mb-4">
            <div class="card h-100">
              <div class="card-header bg-transparent">
                <div class="d-flex justify-content-between align-items-center">
                  <h5 class="card-title mb-0">Tax Brackets</h5>
                  <button @click="showTaxBracketsModal" class="btn btn-sm btn-primary">
                    <i class="mdi mdi-cog-outline me-1"></i> Manage
                  </button>
                </div>
              </div>
              <div class="card-body">
                <div class="text-center py-3">
                  <i class="mdi mdi-chart-histogram text-info" style="font-size: 48px;"></i>
                  <h6 class="mt-3">Tax Bracket Management</h6>
                  <p class="text-muted mb-0">Configure tax bracket structure for income tax calculations</p>
                </div>
                <div class="mt-3">
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Define income ranges for taxation</p>
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Set tax rates for each bracket</p>
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Manage tax relief and exemptions</p>
                </div>
                <div class="mt-3 text-center">
                  <button @click="showTaxBracketsModal" class="btn btn-outline-primary btn-sm">
                    <i class="mdi mdi-plus me-1"></i> Configure Tax Brackets
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Statutory Deductions -->
          <div class="col-xl-4 col-md-6 mb-4">
            <div class="card h-100">
              <div class="card-header bg-transparent">
                <div class="d-flex justify-content-between align-items-center">
                  <h5 class="card-title mb-0">Statutory Deductions</h5>
                  <button @click="showStatutoryDeductionsModal" class="btn btn-sm btn-primary">
                    <i class="mdi mdi-cog-outline me-1"></i> Manage
                  </button>
                </div>
              </div>
              <div class="card-body">
                <div class="text-center py-3">
                  <i class="mdi mdi-gavel text-warning" style="font-size: 48px;"></i>
                  <h6 class="mt-3">Statutory Deduction Management</h6>
                  <p class="text-muted mb-0">Configure mandatory statutory deductions like NSSF, NHIF, etc.</p>
                </div>
                <div class="mt-3">
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Setup tiered calculation methods</p>
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Configure percentage-based deductions</p>
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Manage fixed amount contributions</p>
                </div>
                <div class="mt-3 text-center">
                  <button @click="showStatutoryDeductionsModal" class="btn btn-outline-primary btn-sm">
                    <i class="mdi mdi-plus me-1"></i> Configure Statutory Deductions
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Salary Structure -->
          <div class="col-xl-4 col-md-6 mb-4">
            <div class="card h-100">
              <div class="card-header bg-transparent">
                <div class="d-flex justify-content-between align-items-center">
                  <h5 class="card-title mb-0">Salary Structures</h5>
                  <button @click="showSalaryStructuresModal" class="btn btn-sm btn-primary">
                    <i class="mdi mdi-cog-outline me-1"></i> Manage
                  </button>
                </div>
              </div>
              <div class="card-body">
                <div class="text-center py-3">
                  <i class="mdi mdi-file-tree-outline text-primary" style="font-size: 48px;"></i>
                  <h6 class="mt-3">Salary Structure Management</h6>
                  <p class="text-muted mb-0">Define salary structures and components for organization</p>
                </div>
                <div class="mt-3">
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Create different salary structures</p>
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Link structures with departments</p>
                  <p class="mb-1"><i class="mdi mdi-circle-medium text-success"></i> Setup default benefits package</p>
                </div>
                <div class="mt-3 text-center">
                  <button @click="showSalaryStructuresModal" class="btn btn-outline-primary btn-sm">
                    <i class="mdi mdi-plus me-1"></i> Configure Salary Structures
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Chart Card -->
        <div class="row">
          <div class="col-12">
            <div class="card">
              <div class="card-header bg-transparent">
                <h5 class="card-title mb-0">Payroll Configuration Overview</h5>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-8">
                    <div id="payroll-configuration-chart" style="height: 350px;">
                      <!-- Chart will be rendered here -->
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="configuration-stats">
                      <div class="d-flex align-items-center mb-3">
                        <div class="avatar-xs me-3">
                          <span class="avatar-title rounded-circle bg-primary bg-soft text-primary">
                            <i class="mdi mdi-certificate"></i>
                          </span>
                        </div>
                        <h6 class="font-size-14 mb-0">Pay Grades</h6>
                        <div class="ms-auto">
                          <span class="badge bg-light text-primary">{{ payGradesCount }}</span>
                        </div>
                      </div>
                      <div class="d-flex align-items-center mb-3">
                        <div class="avatar-xs me-3">
                          <span class="avatar-title rounded-circle bg-success bg-soft text-success">
                            <i class="mdi mdi-cash-plus"></i>
                          </span>
                        </div>
                        <h6 class="font-size-14 mb-0">Allowance Types</h6>
                        <div class="ms-auto">
                          <span class="badge bg-light text-success">{{ allowanceTypesCount }}</span>
                        </div>
                      </div>
                      <div class="d-flex align-items-center mb-3">
                        <div class="avatar-xs me-3">
                          <span class="avatar-title rounded-circle bg-danger bg-soft text-danger">
                            <i class="mdi mdi-cash-minus"></i>
                          </span>
                        </div>
                        <h6 class="font-size-14 mb-0">Deduction Types</h6>
                        <div class="ms-auto">
                          <span class="badge bg-light text-danger">{{ deductionTypesCount }}</span>
                        </div>
                      </div>
                      <div class="d-flex align-items-center mb-3">
                        <div class="avatar-xs me-3">
                          <span class="avatar-title rounded-circle bg-info bg-soft text-info">
                            <i class="mdi mdi-chart-histogram"></i>
                          </span>
                        </div>
                        <h6 class="font-size-14 mb-0">Tax Brackets</h6>
                        <div class="ms-auto">
                          <span class="badge bg-light text-info">{{ taxBracketsCount }}</span>
                        </div>
                      </div>
                      <div class="d-flex align-items-center">
                        <div class="avatar-xs me-3">
                          <span class="avatar-title rounded-circle bg-warning bg-soft text-warning">
                            <i class="mdi mdi-gavel"></i>
                          </span>
                        </div>
                        <h6 class="font-size-14 mb-0">Statutory Deductions</h6>
                        <div class="ms-auto">
                          <span class="badge bg-light text-warning">{{ statutoryDeductionsCount }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <PayGradesManagement ref="payGradesManagement" />
    <AllowanceTypesManagementComplete ref="allowanceTypesManagement" />
    <DeductionTypesManagement ref="deductionTypesManagement" />
    <TaxBracketManagement ref="taxBracketManagement" />
    <StatutoryDeductionManagement ref="statutoryDeductionManagement" />
    <SalaryStructureManagement ref="salaryStructureManagement" />
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import PayGradesManagement from "@/components/payroll/PayGradesManagement.vue";
import AllowanceTypesManagementComplete from "@/components/payroll/AllowanceTypesManagement.vue";
import DeductionTypesManagement from "@/components/payroll/DeductionTypesManagement.vue";
import TaxBracketManagement from "@/components/payroll/TaxBracketManagement.vue";
import StatutoryDeductionManagement from "@/components/payroll/StatutoryDeductionManagement.vue";
import SalaryStructureManagement from "@/components/payroll/SalaryStructureManagement.vue";
import NavigationBar from "@/components/Navigation.vue";

// Import chart library if needed
// import Chart from 'chart.js/auto';

export default {
  name: 'PayrollSetupManagement',
  components: {
    NavigationBar,
    PayGradesManagement,
    AllowanceTypesManagementComplete,
    DeductionTypesManagement,
    TaxBracketManagement,
    StatutoryDeductionManagement,
    SalaryStructureManagement
  },
  data() {
    return {
      loading: false,
      payGradesCount: 0,
      allowanceTypesCount: 0,
      deductionTypesCount: 0,
      taxBracketsCount: 0,
      statutoryDeductionsCount: 0,
      salaryStructuresCount: 0,
      // Chart data can be added here if needed
      chartData: null
    };
  },
  mounted() {
    this.getPayrollSetupCounts();
    // Initialize chart if needed
    // this.initChart();
  },
  methods: {
    getPayrollSetupCounts() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getPayrollSetupCounts");

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              const counts = res.data.data;
              this.payGradesCount = counts.payGradesCount || 0;
              this.allowanceTypesCount = counts.allowanceTypesCount || 0;
              this.deductionTypesCount = counts.deductionTypesCount || 0;
              this.taxBracketsCount = counts.taxBracketsCount || 0;
              this.statutoryDeductionsCount = counts.statutoryDeductionsCount || 0;
              this.salaryStructuresCount = counts.salaryStructuresCount || 0;

              // Update chart if needed
              // this.updateChart(counts);
            } else {
              console.error("Error fetching payroll setup counts:", res.data.message);
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
          });
    },
    showPayGradesModal() {
      this.$refs.payGradesManagement.showModal();
    },
    showAllowanceTypesModal() {
      this.$refs.allowanceTypesManagement.showModal();
    },
    showDeductionTypesModal() {
      this.$refs.deductionTypesManagement.showModal();
    },
    showTaxBracketsModal() {
      this.$refs.taxBracketManagement.showModal();
    },
    showStatutoryDeductionsModal() {
      this.$refs.statutoryDeductionManagement.showModal();
    },
    showSalaryStructuresModal() {
      this.$refs.salaryStructureManagement.showModal();
    },
    /*
    // You can uncomment and implement chart functionality if needed
    initChart() {
      // Initialize chart with placeholder data
      const ctx = document.getElementById('payroll-configuration-chart');
      if (ctx) {
        this.chart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: ['Pay Grades', 'Allowances', 'Deductions', 'Tax Brackets', 'Statutory Deductions'],
            datasets: [{
              label: 'Configuration Count',
              data: [
                this.payGradesCount,
                this.allowanceTypesCount,
                this.deductionTypesCount,
                this.taxBracketsCount,
                this.statutoryDeductionsCount
              ],
              backgroundColor: [
                'rgba(78, 115, 223, 0.2)',
                'rgba(40, 167, 69, 0.2)',
                'rgba(220, 53, 69, 0.2)',
                'rgba(54, 185, 204, 0.2)',
                'rgba(246, 194, 62, 0.2)'
              ],
              borderColor: [
                'rgba(78, 115, 223, 1)',
                'rgba(40, 167, 69, 1)',
                'rgba(220, 53, 69, 1)',
                'rgba(54, 185, 204, 1)',
                'rgba(246, 194, 62, 1)'
              ],
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                precision: 0
              }
            }
          }
        });
      }
    },
    updateChart(counts) {
      if (this.chart) {
        this.chart.data.datasets[0].data = [
          counts.payGradesCount || 0,
          counts.allowanceTypesCount || 0,
          counts.deductionTypesCount || 0,
          counts.taxBracketsCount || 0,
          counts.statutoryDeductionsCount || 0
        ];
        this.chart.update();
      }
    }
    */
  }
};
</script>

<style scoped>
.avatar-xs {
  height: 2rem;
  width: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.configuration-stats .d-flex {
  padding: 10px;
  border-radius: 5px;
  transition: all 0.3s;
}

.configuration-stats .d-flex:hover {
  background-color: #f8f9fa;
}
</style>